import React, { useState, useEffect } from 'react';
import {
  getAllPackages,
  createPackage,
  updatePackage,
  deletePackage,
  togglePackageStatus,
  getDefaultPackageTemplate,
  PACKAGE_TYPES,
  PACKAGE_STATUS
} from '../services/packageService';
import LoadingSpinner from './LoadingSpinner';

// Predefined package features for employers/speakers
const PREDEFINED_EMPLOYER_FEATURES = [
  'Unlimited Job Applications',
  '10 Days Job Validity',
  '15 Days Job Validity',
  '30 Days Job Validity',
  '60 Days Job Posting',
  '1 Job Posting',
  '2 Job Posting',
  '3 Job Posting',
  '10 Job Postings',
  'Premium Listing Position',
  '5 Pre Screened Leads',
  '20 Database Candidates'
];

// Predefined package features for franchises
const PREDEFINED_FRANCHISE_FEATURES = [
  '50 Employer Accounts',
  '100 Employer Accounts',
  'Unlimited Employer Accounts',
  'Dedicated Support Manager',
  'White Label Branding',
  'API Access',
  'Monthly Performance Reports',
  'Custom Domain Integration'
];

const PackageManagement = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [formData, setFormData] = useState(getDefaultPackageTemplate(PACKAGE_TYPES.JOB_SEEKER));
  const [submitting, setSubmitting] = useState(false);
  const [newFeature, setNewFeature] = useState('');
  const [filterType, setFilterType] = useState('all'); // 'all', 'job_seeker', 'employer'
  const [selectedPredefinedFeatures, setSelectedPredefinedFeatures] = useState([]);

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    setLoading(true);
    const result = await getAllPackages();
    if (result.success) {
      setPackages(result.packages);
    }
    setLoading(false);
  };

  const getPredefinedFeatures = (type) => {
    if (type === PACKAGE_TYPES.EMPLOYER) return PREDEFINED_EMPLOYER_FEATURES;
    if (type === PACKAGE_TYPES.FRANCHISE) return PREDEFINED_FRANCHISE_FEATURES;
    return [];
  };

  const handleCreateNew = (type) => {
    setEditingPackage(null);
    setFormData(getDefaultPackageTemplate(type));
    setSelectedPredefinedFeatures([]);
    setShowForm(true);
  };

  const handleEdit = (pkg) => {
    setEditingPackage(pkg);
    setFormData({
      ...pkg
    });
    // Set selected predefined features based on existing features
    const predefinedInPackage = (pkg.features || []).filter(f =>
      PREDEFINED_EMPLOYER_FEATURES.includes(f)
    );
    setSelectedPredefinedFeatures(predefinedInPackage);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      let result;
      if (editingPackage) {
        result = await updatePackage(editingPackage.id, formData);
      } else {
        result = await createPackage(formData);
      }

      if (result.success) {
        setShowForm(false);
        setEditingPackage(null);
        fetchPackages();
        alert(`Package ${editingPackage ? 'updated' : 'created'} successfully! Notifications have been sent to relevant users.`);
      } else {
        alert('Error: ' + result.error);
      }
    } catch (error) {
      alert('Error: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (packageId) => {
    if (window.confirm('Are you sure you want to delete this package?')) {
      const result = await deletePackage(packageId);
      if (result.success) {
        fetchPackages();
      } else {
        alert('Error deleting package: ' + result.error);
      }
    }
  };

  const handleToggleStatus = async (pkg) => {
    const result = await togglePackageStatus(pkg.id, pkg.status);
    if (result.success) {
      fetchPackages();
    } else {
      alert('Error updating status: ' + result.error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCustomFreeChange = (checked) => {
    setFormData(prev => {
      let updates = {
        isCustomFree: checked,
        price: checked ? 0 : prev.price
      };

      if (checked) {
        // Add free features based on type
        let features = [...(prev.features || [])];
        if (prev.type === PACKAGE_TYPES.EMPLOYER) {
          updates.maxJobPostings = 999999;
          updates.featuredJobs = 100;
          if (!features.includes('Unlimited Job Postings')) features.push('Unlimited Job Postings');
        } else if (prev.type === PACKAGE_TYPES.JOB_SEEKER) {
          updates.maxApplications = 999999;
          if (!features.includes('Unlimited Job Applications')) features.push('Unlimited Job Applications');
        } else if (prev.type === PACKAGE_TYPES.FRANCHISE) {
          updates.maxUsers = 999999;
          if (!features.includes('Unlimited Employer Accounts')) features.push('Unlimited Employer Accounts');
        }
        updates.features = features;
      }

      return { ...prev, ...updates };
    });
  };

  const togglePredefinedFeature = (feature) => {
    setSelectedPredefinedFeatures(prev => {
      if (prev.includes(feature)) {
        // Remove from selected
        return prev.filter(f => f !== feature);
      } else {
        // Add to selected
        return [...prev, feature];
      }
    });
  };

  const addFeature = () => {
    if (newFeature.trim()) {
      // Check if it's not already in predefined features
      const customFeatures = (formData.features || []).filter(f =>
        !PREDEFINED_EMPLOYER_FEATURES.includes(f)
      );
      setFormData(prev => ({
        ...prev,
        features: [...selectedPredefinedFeatures, ...customFeatures, newFeature.trim()]
      }));
      setNewFeature('');
    }
  };

  const removeFeature = (index) => {
    const featureToRemove = formData.features[index];
    // If it's a predefined feature, remove from selected list
    const predefinedFeatures = getPredefinedFeatures(formData.type);
    if (predefinedFeatures.includes(featureToRemove)) {
      setSelectedPredefinedFeatures(prev => prev.filter(f => f !== featureToRemove));
    }
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  // Sync selected predefined features with formData
  useEffect(() => {
    if (showForm) {
      const customFeatures = (formData.features || []).filter(f =>
        !PREDEFINED_EMPLOYER_FEATURES.includes(f)
      );
      setFormData(prev => ({
        ...prev,
        features: [...selectedPredefinedFeatures, ...customFeatures]
      }));
    }
  }, [selectedPredefinedFeatures]);

  // Filter packages based on selected type
  const filteredPackages = packages.filter(pkg => {
    if (filterType === 'all') return true;
    return pkg.type === filterType;
  });

  if (loading) return <LoadingSpinner />;

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-8 relative overflow-hidden pb-16">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-80 h-80 bg-[radial-gradient(#0e3a61_1px,transparent_1px)] [background-size:20px_20px] opacity-5 pointer-events-none -z-10"></div>

      {/* Header Banner */}
      <div className="bg-gradient-to-r from-[#0e3a61] via-[#0b2d4b] to-[#051728] rounded-[2rem] rounded-tr-[4rem] rounded-bl-[4rem] p-8 text-white shadow-xl relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-20 translate-x-20 pointer-events-none"></div>
        <div className="absolute -bottom-10 -left-10 w-48 h-48 bg-[#e2822f]/10 rounded-full blur-2xl pointer-events-none"></div>
        <div className="relative z-10 space-y-2">
          <span className="text-[10px] uppercase font-bold tracking-widest text-[#e2822f] bg-[#e2822f]/15 px-3 py-1 rounded-full border border-[#e2822f]/20">
            Commercial Templates
          </span>
          <h1 className="text-3xl font-black tracking-tight mt-2">Pricing Tier Configuration</h1>
          <p className="text-slate-350 text-xs sm:text-sm max-w-2xl leading-relaxed">Design, deploy, and regulate subscription packages for candidates, employers, and speaker channels.</p>
        </div>
      </div>

      {/* Filter and Create Section */}
      <div className="bg-white rounded-[2rem] border-2 border-slate-100 p-6 shadow-md flex flex-col md:flex-row justify-between items-center gap-6">
        {/* Filter Dropdown */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 w-full md:w-auto">
          <label className="text-xs font-extrabold text-slate-500 uppercase tracking-widest flex-shrink-0">Categorize by Profile:</label>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm font-semibold text-slate-700 bg-slate-50/50"
          >
            <option value="all">All Commercial Tiers ({packages.length})</option>
            <option value={PACKAGE_TYPES.JOB_SEEKER}>
              Candidate Subscriptions ({packages.filter(p => p.type === PACKAGE_TYPES.JOB_SEEKER).length})
            </option>
            <option value={PACKAGE_TYPES.EMPLOYER}>
              Employer Tiers ({packages.filter(p => p.type === PACKAGE_TYPES.EMPLOYER).length})
            </option>
            <option value={PACKAGE_TYPES.FRANCHISE}>
              Agency Plans ({packages.filter(p => p.type === PACKAGE_TYPES.FRANCHISE).length})
            </option>
          </select>
        </div>

        {/* Create Package Buttons */}
        <div className="flex flex-wrap items-center gap-3 w-full md:w-auto justify-end">
          <button
            onClick={() => handleCreateNew(PACKAGE_TYPES.JOB_SEEKER)}
            className="px-5 py-2.5 bg-gradient-to-r from-[#0e3a61] to-[#09253f] hover:from-[#09253f] hover:to-[#051728] text-white font-extrabold text-xs uppercase tracking-wider rounded-xl shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200"
          >
            + Add Candidate Plan
          </button>
          <button
            onClick={() => handleCreateNew(PACKAGE_TYPES.EMPLOYER)}
            className="px-5 py-2.5 bg-gradient-to-r from-[#508d0c] to-[#3f7009] hover:from-[#3f7009] hover:to-[#2e5207] text-white font-extrabold text-xs uppercase tracking-wider rounded-xl shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200"
          >
            + Add Employer Plan
          </button>
        </div>
      </div>

      {/* Package Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
          <div className="bg-white rounded-[2.5rem] w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl border border-slate-100 flex flex-col">
            
            {/* Modal Title Banner */}
            <div className="relative bg-gradient-to-r from-[#0e3a61] to-[#051728] p-6 text-white flex-shrink-0">
              <h2 className="text-xl font-black">
                {editingPackage ? 'Modify Plan Details' : 'Deploy Commercial Tier'}
              </h2>
              <p className="text-xs text-slate-350 mt-1">Configure parameters and active inclusion features for this subscription plan.</p>
            </div>

            <form onSubmit={handleSubmit} className="p-8 overflow-y-auto space-y-6 flex-1 bg-slate-50/30">
              {/* Custom Free Option */}
              <div className="flex items-start p-4 bg-blue-50/50 rounded-2xl border-2 border-blue-200/50">
                <input
                  type="checkbox"
                  id="customFree"
                  checked={formData.isCustomFree || false}
                  onChange={(e) => handleCustomFreeChange(e.target.checked)}
                  className="w-5 h-5 text-[#0e3a61] rounded border-slate-300 focus:ring-primary-500 cursor-pointer mt-0.5"
                />
                <div className="ml-3.5">
                  <label htmlFor="customFree" className="text-sm font-extrabold text-slate-900 cursor-pointer block">
                    Provision as Complimentary Tier
                  </label>
                  <p className="text-xs text-slate-550 leading-relaxed mt-0.5">
                    Instantly overrides price to zero and grants maximized utility quotas.
                  </p>
                </div>
              </div>

              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-extrabold text-slate-505 uppercase tracking-widest mb-1.5">
                    Subscription Label *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm font-semibold text-slate-700 bg-white"
                    placeholder="e.g. Basic Plan, Platinum Tier"
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-extrabold text-slate-505 uppercase tracking-widest mb-1.5">
                    Profile Segment *
                  </label>
                  <select
                    required
                    value={formData.type}
                    onChange={(e) => handleInputChange('type', e.target.value)}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm font-semibold text-slate-700 bg-white"
                  >
                    <option value={PACKAGE_TYPES.JOB_SEEKER}>Job Seeker</option>
                    <option value={PACKAGE_TYPES.EMPLOYER}>Employer</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-[10px] font-extrabold text-slate-550 uppercase tracking-widest mb-1.5">
                  Tier Summary *
                </label>
                <textarea
                  required
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  rows={3}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm font-semibold text-slate-700 bg-white"
                  placeholder="Summarize the value and limitations of this plan..."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-extrabold text-slate-550 uppercase tracking-widest mb-1.5">
                    Tariff (INR) *
                  </label>
                  <input
                    type="number"
                    required
                    min="0"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => handleInputChange('price', parseFloat(e.target.value) || 0)}
                    disabled={formData.isCustomFree}
                    className={`w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm font-semibold text-slate-700 ${formData.isCustomFree ? 'bg-slate-100 text-slate-400 cursor-not-allowed' : 'bg-white'}`}
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-extrabold text-slate-550 uppercase tracking-widest mb-1.5">
                    Validity Horizon (Days) *
                  </label>
                  <input
                    type="number"
                    required
                    min="1"
                    value={formData.duration}
                    onChange={(e) => handleInputChange('duration', parseInt(e.target.value) || 30)}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm font-semibold text-slate-700 bg-white"
                  />
                </div>
              </div>

              {/* Package-specific fields */}
              {formData.type === PACKAGE_TYPES.JOB_SEEKER && (
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-[10px] font-extrabold text-slate-500 uppercase tracking-widest mb-1.5 flex justify-between items-center">
                      <span>Application Quota Limit *</span>
                      <label className="flex items-center gap-1.5 cursor-pointer text-xs font-extrabold text-[#0e3a61]">
                        <input
                          type="checkbox"
                          checked={formData.maxApplications === 999999 || formData.isCustomFree}
                          onChange={(e) => handleInputChange('maxApplications', e.target.checked ? 999999 : 10)}
                          disabled={formData.isCustomFree}
                          className="rounded text-[#0e3a61] focus:ring-primary-500 w-3.5 h-3.5"
                        />
                        Unlimited Quota
                      </label>
                    </label>
                    <input
                      type={(formData.maxApplications === 999999 || formData.isCustomFree) ? "text" : "number"}
                      value={(formData.maxApplications === 999999 || formData.isCustomFree) ? 'Unlimited' : (formData.maxApplications || 0)}
                      onChange={(e) => {
                        if (!formData.isCustomFree && formData.maxApplications !== 999999) {
                          handleInputChange('maxApplications', parseInt(e.target.value) || 0);
                        }
                      }}
                      disabled={formData.isCustomFree || formData.maxApplications === 999999}
                      className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm font-semibold text-slate-700 bg-white disabled:bg-slate-100 disabled:text-slate-450"
                      required
                    />
                  </div>
                </div>
              )}

              {formData.type === PACKAGE_TYPES.EMPLOYER && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[10px] font-extrabold text-slate-500 uppercase tracking-widest mb-1.5 flex justify-between items-center">
                      <span>Job Listing Quota *</span>
                      <label className="flex items-center gap-1.5 cursor-pointer text-xs font-extrabold text-[#0e3a61]">
                        <input
                          type="checkbox"
                          checked={formData.maxJobPostings === 999999 || formData.isCustomFree}
                          onChange={(e) => handleInputChange('maxJobPostings', e.target.checked ? 999999 : 5)}
                          disabled={formData.isCustomFree}
                          className="rounded text-[#0e3a61] focus:ring-primary-500 w-3.5 h-3.5"
                        />
                        Unlimited Listings
                      </label>
                    </label>
                    <input
                      type={(formData.maxJobPostings === 999999 || formData.isCustomFree) ? "text" : "number"}
                      value={(formData.maxJobPostings === 999999 || formData.isCustomFree) ? 'Unlimited' : (formData.maxJobPostings || 0)}
                      onChange={(e) => {
                        if (!formData.isCustomFree && formData.maxJobPostings !== 999999) {
                          handleInputChange('maxJobPostings', parseInt(e.target.value) || 0);
                        }
                      }}
                      disabled={formData.isCustomFree || formData.maxJobPostings === 999999}
                      className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm font-semibold text-slate-700 bg-white disabled:bg-slate-100 disabled:text-slate-450"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-[10px] font-extrabold text-slate-500 uppercase tracking-widest mb-1.5">
                      Premium Visibility Quota
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={formData.featuredJobs || 0}
                      onChange={(e) => handleInputChange('featuredJobs', parseInt(e.target.value) || 0)}
                      className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm font-semibold text-slate-700 bg-white"
                    />
                  </div>

                  <div className="flex items-center p-3 bg-slate-50 rounded-xl border border-slate-150">
                    <input
                      type="checkbox"
                      id="candidateSearch"
                      checked={formData.candidateSearch || false}
                      onChange={(e) => handleInputChange('candidateSearch', e.target.checked)}
                      className="w-4.5 h-4.5 text-[#0e3a61] rounded border-slate-350 focus:ring-primary-500 cursor-pointer"
                    />
                    <label htmlFor="candidateSearch" className="ml-2.5 text-xs font-extrabold text-slate-700 cursor-pointer">
                      Database Search Access
                    </label>
                  </div>

                  <div className="flex items-center p-3 bg-slate-50 rounded-xl border border-slate-150">
                    <input
                      type="checkbox"
                      id="analyticsAccess"
                      checked={formData.analyticsAccess || false}
                      onChange={(e) => handleInputChange('analyticsAccess', e.target.checked)}
                      className="w-4.5 h-4.5 text-[#0e3a61] rounded border-slate-350 focus:ring-primary-500 cursor-pointer"
                    />
                    <label htmlFor="analyticsAccess" className="ml-2.5 text-xs font-extrabold text-slate-700 cursor-pointer">
                      Data Dashboard Insights
                    </label>
                  </div>
                </div>
              )}

              {/* Features Section */}
              <div className="space-y-4">
                <label className="block text-[10px] font-extrabold text-slate-500 uppercase tracking-widest">
                  Tier Inclusions & Features
                </label>

                {/* Add Custom Feature */}
                <div className="p-4 bg-slate-50 rounded-2xl border border-slate-150 space-y-2.5">
                  <p className="text-xs font-extrabold text-slate-700">Add New Inclusion Feature:</p>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newFeature}
                      onChange={(e) => setNewFeature(e.target.value)}
                      placeholder="Type a feature highlight and press enter..."
                      className="flex-1 px-4 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm font-semibold text-slate-700 bg-white"
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature())}
                    />
                    <button
                      type="button"
                      onClick={addFeature}
                      className="px-4 py-2 bg-[#0e3a61] text-white font-bold text-sm rounded-xl hover:bg-[#09253f] transition-colors"
                    >
                      Add
                    </button>
                  </div>
                </div>

                {/* Features List - Predefined and Custom */}
                {(formData.type === PACKAGE_TYPES.EMPLOYER || formData.type === PACKAGE_TYPES.FRANCHISE) && (
                  <div className="border border-slate-200 rounded-2xl overflow-hidden bg-white shadow-sm">
                    {/* Selected Features Section */}
                    {formData.features && formData.features.length > 0 && (
                      <div className="bg-[#508d0c]/5 border-b border-slate-200">
                        <div className="px-4 py-2.5 bg-[#508d0c]/10 border-b border-[#508d0c]/15">
                          <p className="text-xs font-extrabold text-[#3f7009] uppercase tracking-wider">✓ Active Features List ({formData.features.length})</p>
                        </div>
                        <div className="max-h-60 overflow-y-auto p-3 space-y-2">
                          {formData.features.map((feature, index) => (
                            <div key={index} className="flex items-center justify-between bg-white p-3 rounded-xl border border-slate-150 hover:bg-[#508d0c]/5 transition-colors">
                              <div className="flex items-center flex-1">
                                <svg className="w-4 h-4 text-[#508d0c] mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                                <span className="text-xs font-semibold text-slate-800">{feature}</span>
                              </div>
                              <button
                                type="button"
                                onClick={() => removeFeature(index)}
                                className="text-red-500 hover:text-red-700 transition-colors ml-2 p-1 hover:bg-red-50 rounded animate-pulse"
                                title="Remove feature"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Available Features Section - Grid with Checkboxes */}
                    <div className="bg-slate-50">
                      <div className="px-4 py-2.5 bg-slate-100 border-b border-slate-200">
                        <p className="text-xs font-extrabold text-slate-600 uppercase tracking-wider">Available Features Library ({getPredefinedFeatures(formData.type).length})</p>
                      </div>
                      <div className="p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {getPredefinedFeatures(formData.type).map((feature, idx) => {
                            const isSelected = formData.features && formData.features.includes(feature);
                            return (
                              <label
                                key={idx}
                                className={`flex items-center p-3 rounded-xl border-2 cursor-pointer transition-all ${isSelected
                                  ? 'bg-[#508d0c]/5 border-[#508d0c]/30 hover:bg-[#508d0c]/10'
                                  : 'bg-white border-slate-200 hover:border-blue-300 hover:bg-blue-50'
                                  }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => togglePredefinedFeature(feature)}
                                  className="w-4 h-4 text-blue-600 border-slate-350 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer"
                                />
                                <span className={`ml-3 text-xs ${isSelected ? 'text-[#3f7009] font-extrabold' : 'text-slate-650 font-semibold'}`}>
                                  {feature}
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* For Job Seeker packages - simple list */}
                {formData.type === PACKAGE_TYPES.JOB_SEEKER && formData.features && formData.features.length > 0 && (
                  <div className="space-y-2.5">
                    <p className="text-[10px] font-extrabold text-slate-500 uppercase tracking-widest">Added Features ({formData.features.length}):</p>
                    <div className="max-h-40 overflow-y-auto border border-slate-200 rounded-2xl p-2 space-y-1.5 bg-white">
                      {formData.features.map((feature, index) => (
                        <div key={index} className="flex items-center justify-between bg-slate-50 p-2.5 rounded-xl border border-slate-150">
                          <div className="flex items-center">
                            <svg className="w-4 h-4 text-green-600 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            <span className="text-xs font-semibold text-slate-700">{feature}</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeFeature(index)}
                            className="text-red-500 hover:text-red-750 transition-colors p-1 hover:bg-red-50 rounded"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Form Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-150">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-5 py-2.5 text-slate-550 border border-slate-200 rounded-xl hover:bg-slate-50 font-bold text-xs uppercase tracking-wider transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-5 py-2.5 bg-gradient-to-r from-[#0e3a61] to-[#09253f] text-white font-bold text-xs uppercase tracking-wider rounded-xl hover:shadow-lg disabled:opacity-50 transition-all"
                >
                  {submitting ? 'Saving...' : (editingPackage ? 'Modify Plan Details' : 'Deploy Commercial Tier')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Packages List - Premium Billing Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {filteredPackages.map((pkg, index) => {
          const isActive = pkg.status === PACKAGE_STATUS.ACTIVE;
          return (
            <div
              key={pkg.id}
              className={`relative bg-gradient-to-br from-white via-slate-50/50 to-slate-100/30 rounded-[2.5rem] rounded-tr-[4rem] rounded-bl-[4rem] shadow-xl border-2 transition-all duration-350 hover:shadow-2xl hover:-translate-y-1.5 flex flex-col justify-between overflow-hidden ${
                isActive
                  ? 'border-[#508d0c] shadow-[0_10px_30px_rgba(80,141,12,0.1)]'
                  : 'border-slate-150'
              }`}
            >
              {/* Type Ribbon Badge */}
              <div className="absolute top-4 left-4 z-10">
                <span className={`px-3.5 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest text-white shadow-md ${
                  pkg.type === PACKAGE_TYPES.JOB_SEEKER ? 'bg-[#0e3a61]' :
                  pkg.type === PACKAGE_TYPES.EMPLOYER ? 'bg-[#508d0c]' :
                  'bg-[#e2822f]'
                }`}>
                  {pkg.type === PACKAGE_TYPES.JOB_SEEKER ? 'Candidate' : pkg.type === PACKAGE_TYPES.EMPLOYER ? 'Employer' : 'Agency'}
                </span>
              </div>

              {/* Status Indicator */}
              <div className="absolute top-4 right-4 z-10">
                <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-wider shadow-sm ${
                  isActive ? 'bg-[#508d0c]/10 text-[#508d0c]' : 'bg-slate-200 text-slate-550'
                }`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${isActive ? 'bg-[#508d0c] animate-ping' : 'bg-slate-400'}`}></span>
                  {isActive ? 'Active' : 'Inactive'}
                </span>
              </div>

              {/* Card Body */}
              <div className="p-8 pt-16 flex-1 flex flex-col justify-between space-y-6">
                
                {/* Header info */}
                <div className="text-center space-y-3.5">
                  <h3 className="text-xl font-extrabold text-slate-800 tracking-tight">{pkg.name}</h3>
                  <div className="flex items-baseline justify-center">
                    <span className="text-4xl font-black text-slate-900">
                      ₹{pkg.price}
                    </span>
                    <span className="text-slate-400 text-xs font-bold uppercase tracking-wider ml-1.5">
                      / {pkg.duration} Days
                    </span>
                  </div>
                  {pkg.description && (
                    <p className="text-slate-500 text-xs leading-relaxed max-w-xs mx-auto italic">
                      {pkg.description}
                    </p>
                  )}
                </div>

                {/* Feature checklist (Centered / Boxed) */}
                <div className="bg-slate-50/50 rounded-[2rem] border border-slate-100 p-5 space-y-4">
                  <p className="text-[9px] font-extrabold text-slate-450 uppercase tracking-widest text-center border-b border-slate-150 pb-2">Included Utility Quotas</p>
                  
                  {/* Job Seeker specific variables */}
                  {pkg.type === PACKAGE_TYPES.JOB_SEEKER && pkg.maxApplications !== undefined && (
                    <div className="flex items-center gap-2.5 justify-center py-1 text-slate-700">
                      <svg className="w-4 h-4 text-[#0e3a61] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <span className="font-extrabold text-xs">
                        {pkg.maxApplications === 999999 ? 'Unlimited' : pkg.maxApplications} Applications Limit
                      </span>
                    </div>
                  )}

                  {/* Employer specific variables */}
                  {pkg.type === PACKAGE_TYPES.EMPLOYER && (
                    <div className="space-y-2">
                      {pkg.maxJobPostings !== undefined && (
                        <div className="flex items-center gap-2.5 justify-center text-slate-700">
                          <svg className="w-4 h-4 text-[#0e3a61]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                          <span className="font-extrabold text-xs">
                            {pkg.maxJobPostings === 999999 ? 'Unlimited' : pkg.maxJobPostings} Job Postings
                          </span>
                        </div>
                      )}
                      {pkg.featuredJobs > 0 && (
                        <div className="flex items-center gap-2.5 justify-center text-slate-700">
                          <svg className="w-4 h-4 text-[#e2822f]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.519 4.674c.3.921-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.519-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.915a1 1 0 00.95-.69l1.519-4.674z" />
                          </svg>
                          <span className="font-extrabold text-xs">{pkg.featuredJobs} Premium Position Visibility</span>
                        </div>
                      )}
                      {pkg.candidateSearch && (
                        <div className="flex items-center gap-2.5 justify-center text-slate-700">
                          <svg className="w-4 h-4 text-[#508d0c]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M8 16l2.879-2.879m0 0a3 3 0 104.243-4.242 3 3 0 00-4.243 4.242zM21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="font-extrabold text-xs">Direct Candidate Search Enabled</span>
                        </div>
                      )}
                      {pkg.analyticsAccess && (
                        <div className="flex items-center gap-2.5 justify-center text-slate-700">
                          <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                          </svg>
                          <span className="font-extrabold text-xs">Live Platform Analytics Portal</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Predefined / Custom lists */}
                  {pkg.features && pkg.features.length > 0 && (
                    <div className="space-y-1.5 pt-2 border-t border-slate-150">
                      {pkg.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center justify-center gap-2 text-slate-650 text-center">
                          <svg className="w-3.5 h-3.5 text-[#508d0c] flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span className="text-[11px] font-semibold">{feature}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Bottom Actions */}
                <div className="space-y-2.5 pt-4 border-t border-slate-100 mt-auto">
                  <button
                    onClick={() => handleEdit(pkg)}
                    className="w-full py-2.5 px-4 bg-gradient-to-r from-[#0e3a61] to-[#09253f] hover:from-[#09253f] hover:to-[#051728] text-white rounded-xl font-extrabold text-xs uppercase tracking-wider shadow-md hover:shadow-lg transition-all duration-200"
                  >
                    Modify Plan Details
                  </button>

                  <div className="grid grid-cols-2 gap-3.5">
                    <button
                      onClick={() => handleToggleStatus(pkg)}
                      className={`py-2 px-4 rounded-xl font-bold text-[10px] uppercase tracking-wider transition-colors border shadow-sm ${
                        isActive
                          ? 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100'
                          : 'bg-[#508d0c]/5 text-[#508d0c] border-[#508d0c]/25 hover:bg-[#508d0c]/10'
                      }`}
                    >
                      {isActive ? 'Deactivate' : 'Activate'}
                    </button>
                    <button
                      onClick={() => handleDelete(pkg.id)}
                      className="py-2 px-4 bg-red-50 text-red-600 border border-red-100 rounded-xl hover:bg-red-100 font-bold text-[10px] uppercase tracking-wider transition-colors shadow-sm"
                    >
                      Delete
                    </button>
                  </div>
                </div>

              </div>
            </div>
          );
        })}
      </div>

      {filteredPackages.length === 0 && packages.length > 0 && (
        <div className="text-center py-20 bg-white rounded-[2rem] shadow-xl border border-slate-100">
          <div className="w-20 h-20 bg-slate-50 rounded-[2rem] flex items-center justify-center mx-auto mb-6 border border-slate-100 border-dashed">
            <svg className="w-10 h-10 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <h3 className="text-base font-bold text-slate-900 mb-2">No subscription tiers identified</h3>
          <p className="text-xs text-slate-500 mb-6 max-w-sm mx-auto">No profiles match the filter category select choice.</p>
          <button
            onClick={() => setFilterType('all')}
            className="px-6 py-3 bg-[#0e3a61] hover:bg-[#0b2e4e] text-white font-bold text-xs rounded-xl shadow-md transition-all duration-200"
          >
            Show All Commercial Tiers
          </button>
        </div>
      )}

      {packages.length === 0 && (
        <div className="text-center py-20 bg-white rounded-[2rem] shadow-xl border border-slate-100">
          <div className="w-20 h-20 bg-slate-50 rounded-[2rem] flex items-center justify-center mx-auto mb-6 border border-slate-100 border-dashed">
            <svg className="w-10 h-10 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
          </div>
          <h3 className="text-base font-bold text-slate-900 mb-2">No pricing tiers deployed</h3>
          <p className="text-xs text-slate-500 mb-6 max-w-sm mx-auto">Create and deploy your first commercial subscription templates to start.</p>
        </div>
      )}
    </div>
  );
};

export default PackageManagement;
